import { useState, useEffect, useRef } from 'react';
import { Input } from '@/components/ui/FormControls';
import FieldHint from '@/components/common/FieldHint';
import { useConfirm } from '@/contexts/ConfirmContext';
import {
  defaultJerseyEntryConfig,
  emptyJerseyConfig,
  jerseyStarterConfig,
  JERSEY_HELPER_CODES,
  type CatalogDefinition,
} from '@klothiq/domain';

export default function JerseyEntrySettings({
  definition,
  onChange,
  available,
}: {
  definition: CatalogDefinition;
  onChange: (definition: CatalogDefinition) => void;
  available: boolean;
}) {
  const config = definition.jersey_entry || defaultJerseyEntryConfig();
  const [sizeText, setSizeText] = useState(config.size_order.join(', '));
  const ownOrder = useRef(config.size_order.join(','));
  const incomingOrder = config.size_order.join(',');
  useEffect(() => {
    if (ownOrder.current !== incomingOrder) {
      setSizeText(config.size_order.join(', '));
      ownOrder.current = incomingOrder;
    }
  }, [incomingOrder]);
  const confirm = useConfirm();
  return (
    <section className="glass-card space-y-4 p-4 sm:p-6">
      <div>
        <h2 className="font-semibold">Optional jersey entry</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Offer consistent name, code and SKU suggestions while keeping direct entry available.
          Every jersey detail stays optional.
        </p>
      </div>
      {!available && (
        <p className="text-sm text-muted-foreground">
          The optional jersey-entry database update is needed before enabling this setup.
        </p>
      )}
      <label className="flex min-h-11 items-center gap-3 text-sm">
        <Input
          type="checkbox"
          checked={config.enabled}
          disabled={!available}
          helpLabel="Enable optional jersey entry for this category"
          onChange={async (e) => {
            const enabled = e.target.checked;
            if (
              enabled &&
              !(await confirm({
                title: 'Enable optional jersey entry?',
                description:
                  'Jersey helpers will become optional and starter mappings will fill empty sections for review. The previous required SKU review will be disabled for this published version. Existing products and identifiers are kept.',
                confirmText: 'Review optional setup',
              }))
            )
              return;
            onChange({
              ...definition,
              jersey_entry: { ...config, enabled },
              ...(enabled
                ? {
                    fields: definition.fields.map((f) =>
                      JERSEY_HELPER_CODES.has(f.code) ? { ...f, required: false } : f,
                    ),
                    jersey_identifiers: {
                      ...jerseyStarterConfig(definition.jersey_identifiers || emptyJerseyConfig()),
                      enabled: false,
                    },
                  }
                : {}),
            });
          }}
        />
        Enable optional details and independent Auto / Manual entry
      </label>
      {config.enabled && (
        <>
          <div className="grid gap-4 sm:grid-cols-2">
            <label className="space-y-1 text-sm">
              Code prefix without a mapped team
              <Input
                value={config.neutral_prefix}
                maxLength={8}
                helpLabel="Code prefix without a mapped team"
                onChange={(e) =>
                  onChange({
                    ...definition,
                    jersey_entry: { ...config, neutral_prefix: e.target.value.toUpperCase() },
                  })
                }
              />
              <FieldHint>
                2–8 letters/numbers, e.g. JER. New codes receive a unique number on save.
              </FieldHint>
            </label>
            <label className="space-y-1 text-sm">
              Token for missing optional details
              <Input
                value={config.missing_token}
                maxLength={16}
                helpLabel="Token for missing optional details"
                onChange={(e) =>
                  onChange({
                    ...definition,
                    jersey_entry: { ...config, missing_token: e.target.value.toUpperCase() },
                  })
                }
              />
              <FieldHint>
                Used inside generated SKUs; never inserted into the product name.
              </FieldHint>
            </label>
            <label className="space-y-1 text-sm sm:col-span-2">
              Size order for Duplicate
              <Input
                value={sizeText}
                helpLabel="Size order for Duplicate"
                placeholder="S, M, L, XL, XXL"
                onChange={(e) => {
                  const sizes = e.target.value
                    .split(',')
                    .map((v) => v.trim())
                    .filter(Boolean);
                  ownOrder.current = sizes.join(',');
                  setSizeText(e.target.value);
                  onChange({ ...definition, jersey_entry: { ...config, size_order: sizes } });
                }}
              />
              <FieldHint>
                Comma-separated selling sizes in order. Leave blank to use the category’s ordered
                Size choices. No next size means no automatic duplicate row.
              </FieldHint>
            </label>
          </div>
          <p className="text-xs text-muted-foreground">
            Name order: team, period, collection, kit, sleeve, collar, edition, fit, audience, print
            and custom details. Generated names use UPPERCASE with | separators; blank parts are
            omitted. Token mappings below apply only to future issuance.
          </p>
        </>
      )}
    </section>
  );
}
